import React from 'react';
import styles from './knowledge-info.module.scss';
import moment from 'moment';
import { Lang, WARNING_ABOUT_TO_EXPIRY_IN_DAYS, EVENTS, INTERNAL_ROUTING } from 'shared/constants';
import { Modal } from 'src/components/design-system';
import { ExclamationMark } from 'src/images';
import { theme } from 'shared/constants/theme';
import { Breadcrumbs } from 'src/components/breadcrumbs';
import { TagsIcons } from 'src/components/tags/tagsIcons';
import analyticsService from 'src/helpers/analytics.service';
import { TextTag } from 'src/components/tags/text-tags';
import { useDateFormatter } from 'shared/hooks/dates.hooks';
import { Contributors } from 'src/components/knowledge-content/components/contributors';
import { useVettedOrProvidedLang } from 'shared/hooks/lang.hooks';
import { Owners } from 'src/components/common/owners';
import { getFieldVisibility, KNOWLEDGE_VISIBILITY_FIELDS } from 'shared/utils/knowledge.utils';

const KnowledgeInfoComponent = React.memo(function KnowledgeInfoComponent({
  title,
  itemId = null,
  lang = Lang.INFORMATION_MODAL,
  ownerIds,
  expires_at = '',
  expiresAt = '',
  file_updated_at = null,
  fileUpdatedAt = null,
  updatedAt = '',
  knowledgeSetItemDetails = () => {},
  ownerObject = {},
  parents = [],
  tags,
  contributors = [],
  options = null,
  itemCategory = null,
}) {
  const { formatLongDate } = useDateFormatter();
  const validatedByLang = useVettedOrProvidedLang(lang.VALIDATED_BY);
  const lastUpdatedLang = useVettedOrProvidedLang(lang.LAST_UPDATE);
  const vettedAtLang = useVettedOrProvidedLang(lang.VALIDATED_AT);
  const shouldShowField = getFieldVisibility({ options }, itemCategory);
  const showOwners = shouldShowField(KNOWLEDGE_VISIBILITY_FIELDS.OWNERS);
  const hasTags = !!(tags?.specialty || tags?.subspecialty || tags?.contentType);

  const close = React.useCallback(() => {
    analyticsService.track(EVENTS.KNOWLEDGE_ITEMS.CLOSE_INFORMATION, {
      id: itemId,
      title,
      ownerId: ownerObject.id,
      ...tags,
    });
    knowledgeSetItemDetails(null);
  }, [knowledgeSetItemDetails, itemId, tags, title, ownerObject]);

  let expiryDate = moment(expires_at || expiresAt);
  let daysFromExpiry = expiryDate.diff(moment(), 'days');

  let lastUpdated = file_updated_at || fileUpdatedAt || updatedAt;

  let expiryStyle = styles.contentExpiryDateGood;
  let isShowExclamationMark = false;

  if (daysFromExpiry < WARNING_ABOUT_TO_EXPIRY_IN_DAYS) {
    isShowExclamationMark = true;
    expiryStyle = styles.contentExpired;
  }

  const ownerByIdRender = () => {
    if (!ownerObject.id || !showOwners) return null;

    let { firstName, lastName, email, phone } = ownerObject;

    return (
      <>
        {!!firstName && (
          <div className={styles.detail}>
            <h6>{validatedByLang}</h6>
            <span>
              {firstName} {lastName}
            </span>
          </div>
        )}

        {!!email && (
          <div className={styles.detail}>
            <h6>{lang.EMAIL}</h6>
            <a href={`mailto:${email}`} target="_blank" rel="noreferrer">
              <span>{email}</span>
            </a>
          </div>
        )}

        {!!phone && (
          <div className={styles.detail}>
            <h6>{lang.PHONE}</h6>
            <span>{phone}</span>
          </div>
        )}
      </>
    );
  };

  const renderTags = () => (
    <div className={styles.tagsContainer}>
      <TagsIcons
        tags={tags}
        hideTags={{ targetAudience: true, contentType: true }}
        className={styles.tagIcons}
      />
      <TextTag tag={tags?.contentType} />
    </div>
  );

  return (
    <Modal open={!!itemId} title={lang.HEADER} onCancel={close} footer={null} width={600}>
      <div className={styles.body}>
        <div className={styles.mainColumn}>
          {showOwners && (
            <div className={styles.ownersWrapper}>
              <Owners
                ownerIds={ownerIds}
                title={lang.VALIDATED_BY}
                titleClassName={styles.title}
                onNavigate={close}
              />
            </div>
          )}
          {ownerByIdRender()}
          <Contributors
            contributors={contributors}
            onNavigate={close}
            titleClassName={styles.title}
          />

          <div className={styles.data}>
            <div className={styles.detail}>
              <h6>{lang.EXPIRE_DATA}</h6>
              <div className={styles.expiryDate}>
                {!expires_at && !expiresAt ? (
                  <span>{lang.NEVER_EXPIRES}</span>
                ) : (
                  <>
                    <span className={expiryStyle}>{formatLongDate(expiryDate)}</span>
                    {isShowExclamationMark && (
                      <ExclamationMark
                        color={theme.contentExpired}
                        className={styles.exclamationMark}
                      />
                    )}
                  </>
                )}
              </div>
            </div>

            {!!lastUpdated && (
              <div className={styles.detail}>
                <h6>{showOwners ? lastUpdatedLang : vettedAtLang}</h6>
                <div className={styles.expiryDate}>
                  <span>{formatLongDate(lastUpdated)}</span>
                </div>
              </div>
            )}
          </div>

          <div className={styles.detail}>
            <h6>{lang.PARENTS_PATH}</h6>
            {parents.map(category => (
              <Breadcrumbs
                wrapperStyle={styles.breadcrumbs}
                pathParts={category.pathParts}
                title={category.title}
                key={category.id}
                hideRoot
                workspaceName={category.workspaceName}
                initialWorkspaceId={category.workspaceId}
                currentItemId={category.id}
                currentQueryParamKey={INTERNAL_ROUTING.QUERY_PARAMS.KNOWLEDGE}
                sectionLinks
                onPathClick={close}
              />
            ))}
          </div>
        </div>
        {hasTags && renderTags()}
      </div>
    </Modal>
  );
});

export { KnowledgeInfoComponent };
